import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  AppBar, 
  Toolbar,
  Grid,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const Portfolio = () => {
  const navigate = useNavigate();

  return (
    <>
      {/* Navigation Bar - Matching Home & Contact Page Style */}
      <AppBar 
        position="static" 
        sx={{
          backgroundColor: '#062e22', // Dark forest green
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.3)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 300,
                color: 'white',
                fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
                fontSize: '1.8rem',
                letterSpacing: '0.5px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#c7d2cc',
                  transform: 'translateY(-1px)',
                  textShadow: '0 2px 8px rgba(255,255,255,0.2)'
                }
              }}
              onClick={() => navigate('/')}
            >
              Heather Walker Photography
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', // Active state
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: '#90caf9'
                  }
                }}
              >
                Portfolio
              </Button>
              <Button 
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#90caf9'
                  }
                }}
                onClick={() => navigate('/contact')}
              >
                Contact
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          py: 10,
          textAlign: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)',
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography 
            variant="h2" 
            sx={{
              fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
              fontWeight: 300,
              letterSpacing: '1px',
              color: '#062e22', // Your forest green brand color
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Portfolio
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              fontFamily: '"Cormorant Garamond", serif',
              fontWeight: 300,
              color: '#2d5a3d', // Slightly lighter forest green for subtitle
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              mb: 4
            }}
          >
            Capturing life's most precious moments with artistry and passion
          </Typography>
        </Container>
      </Box>

      {/* Portfolio Coming Soon Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left Column - Text */}
          <Grid item xs={12} lg={6}>
            <Typography 
              variant="h3" 
              sx={{
                fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
                fontWeight: 300,
                letterSpacing: '0.5px',
                color: '#062e22',
                mb: 3
              }}
            >
              Portfolio Coming Soon
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 300,
                color: '#666',
                lineHeight: 1.7,
                mb: 4
              }}
            >
              We're currently curating our finest work to showcase the beauty and emotion 
              we capture in every session. Our portfolio will feature stunning examples of 
              family and lifestyle photography that tell your unique story.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#777',
                lineHeight: 1.6,
                fontStyle: 'italic'
              }}
            >
              "Photography is the story I fail to put into words." - Destin Sparks
            </Typography>
          </Grid>

          {/* Right Column - Placeholder */}
          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                height: '400px',
                backgroundColor: '#f5f5f5',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #ccc',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#999',
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: 'italic'
                }}
              >
                Gallery Preview Coming Soon
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Model Call-to-Action Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #062e22 0%, #2d5a3d 100%)',
          py: 10,
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Paper 
            elevation={8}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              color: '#062e22'
            }}
          >
            <Typography 
              variant="h3" 
              sx={{
                fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
                fontWeight: 400,
                letterSpacing: '1px',
                mb: 3,
                color: '#062e22'
              }}
            >
              Seeking Models for Portfolio
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 300,
                color: '#555',
                lineHeight: 1.7,
                mb: 4,
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Are you interested in being part of our creative journey? We're looking for 
              models of all backgrounds and experience levels to collaborate with us in 
              building a stunning portfolio that showcases diverse beauty and authentic moments.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  mb: 2,
                  fontWeight: 500
                }}
              >
                What we offer:
              </Typography>
              <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, textAlign: 'center', boxShadow: 1 }}>
                    <CardContent>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        📸 Professional Photos
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        High-quality images for your portfolio
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, textAlign: 'center', boxShadow: 1 }}>
                    <CardContent>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        🎨 Creative Concepts
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Artistic and fashion-forward shoots
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, textAlign: 'center', boxShadow: 1 }}>
                    <CardContent>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        🤝 Collaborative Spirit
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Professional, fun environment
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                lineHeight: 1.6,
                mb: 4,
                fontStyle: 'italic'
              }}
            >
              No experience necessary • All ages welcome • Various concepts available
            </Typography>

            <Button 
              variant="contained"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 600,
                px: 6,
                py: 2,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '1.2rem',
                boxShadow: '0 6px 25px rgba(25, 118, 210, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 30px rgba(25, 118, 210, 0.5)',
                },
                '&:active': {
                  backgroundColor: '#0d47a1',
                  transform: 'translateY(-1px)',
                }
              }}
            >
              Inquire About Modeling
            </Button>

            <Typography 
              variant="body2" 
              sx={{ 
                color: '#888',
                mt: 3,
                fontStyle: 'italic'
              }}
            >
              Ready to create something beautiful together? Get in touch!
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* Footer CTA */}
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <Typography 
          variant="h5" 
          sx={{
            fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
            fontWeight: 300,
            color: '#062e22',
            mb: 2
          }}
        >
          Ready to Book Your Session?
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#666',
            mb: 3,
            maxWidth: '500px',
            mx: 'auto'
          }}
        >
          Whether you're interested in modeling or booking a photography session, 
          we'd love to hear from you and discuss your vision.
        </Typography>
        <Button 
          variant="outlined"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            borderColor: '#062e22',
            color: '#062e22',
            fontFamily: '"Cormorant Garamond", serif',
            fontWeight: 500,
            px: 4,
            py: 1.5,
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#041f18',
              color: '#041f18',
              backgroundColor: 'rgba(6, 46, 34, 0.04)'
            }
          }}
        >
          Contact Us Today
        </Button>
      </Container>
    </>
  );
};

export default Portfolio;